"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.resolveAccountPreference = resolveAccountPreference;
exports.hasAccountPreference = hasAccountPreference;
exports.saveLanguage = saveLanguage;
const HDSProfile_ts_1 = __importStar(require("./HDSProfile.js"));
const HDSSettings_ts_1 = __importDefault(require("./HDSSettings.js"));
const errors_ts_1 = require("../errors.js");
/**
 * Resolution order for a preference that moved from per-app HDSSettings to account-level
 * HDSProfile (plan 78 §C 7.1b):
 *
 *   1. HDSProfile — only when the value is genuinely **stored**. The account app owns these,
 *      so an explicit account-level value wins.
 *   2. HDSSettings — the legacy per-app value, for apps not yet migrated.
 *   3. HDSProfile defaults (incl. browser inference).
 *
 * Step 1 checks `isStored` rather than `isHooked` on purpose: the profile's preferences always
 * read as a non-null default, so hooking alone would let `DD.MM.YYYY` silently override a
 * date format the user really had set per-app. That would be a regression for any app that
 * already hooks HDSProfile (hds-webapp does).
 */
function resolveAccountPreference(key) {
    if (HDSProfile_ts_1.default.isHooked && HDSProfile_ts_1.default.isStored(key))
        return HDSProfile_ts_1.default.get(key);
    if (HDSSettings_ts_1.default.isHooked)
        return HDSSettings_ts_1.default.get(key);
    return HDSProfile_ts_1.default.get(key);
}
/**
 * Whether *some* source can supply this preference — i.e. it is stored at account level or
 * an app has hooked its settings. Callers use this to keep "nothing hooked" behaviour
 * (e.g. formatEventDate falling back to ISO) instead of silently adopting a default.
 */
function hasAccountPreference(key) {
    return (HDSProfile_ts_1.default.isHooked && HDSProfile_ts_1.default.isStored(key)) || HDSSettings_ts_1.default.isHooked;
}
/**
 * Save the person's language for every HDS app, and for the platform's own mail.
 *
 * Writes two places, as the account app's Preferences page does (decision 2026-07-15): the
 * account-level `preferredLocales` (HDSProfile, read by every HDS app; the language becomes the
 * first and only entry, and the localizer switches to it) and the Pryv core `account.language`
 * (password-reset and other platform mail). They are different systems but a person has one
 * language; writing only the first leaves platform mail in the old language.
 *
 * The core write is best effort: it needs a personal token and must not fail the save, so a
 * refusal resolves with `coreLanguageSaved: false` and `coreError` (show a notice, not an
 * error). The profile write is not: it rejects when HDSProfile is not hooked or the write fails.
 *
 * @param locale     language code, e.g. `fr`.
 * @param opts.connection  connection for `account.update`; defaults to the one HDSProfile is hooked to.
 */
async function saveLanguage(locale, opts = {}) {
    if (typeof locale !== 'string' || locale.trim() === '') {
        throw new errors_ts_1.HDSLibError('saveLanguage: a language code is required', { locale });
    }
    await HDSProfile_ts_1.default.set('preferredLocales', [locale]);
    const connection = opts.connection ?? (0, HDSProfile_ts_1.getHookedProfileConnection)();
    if (connection == null) {
        return { profileSaved: true, coreLanguageSaved: false, coreError: new errors_ts_1.HDSLibError('saveLanguage: no connection for account.update') };
    }
    try {
        const [result] = await connection.api([
            { method: 'account.update', params: { update: { language: locale } } }
        ]);
        if (result == null || result.error) {
            return { profileSaved: true, coreLanguageSaved: false, coreError: result?.error ?? new errors_ts_1.HDSLibError('saveLanguage: no answer to account.update') };
        }
        return { profileSaved: true, coreLanguageSaved: true };
    }
    catch (e) {
        return { profileSaved: true, coreLanguageSaved: false, coreError: e };
    }
}
